import { ldb } from '@codesuma/baseline'
import utils from '../utils'
import supabase from './supabase'

export interface UserHandle {
    user_id: string
    handle: string
    display_name: string | null
}

// Persisted so the menu can show the handle instantly — and offline — on any
// visit after the first. A handle never changes, so the cache only needs to be
// discarded when it belongs to a different user (account switch).
const KEY = 'papa-handle'
let cached: UserHandle | null = ldb.get(KEY) || null

// The cached handle is only trustworthy for the current user. Before auth
// initializes there is no user id yet — this is a single-user device cache, so
// trust it then too (getMine re-checks once a session exists).
const isMine = (h: UserHandle): boolean => {
    const uid = supabase.auth.getUser()?.id
    return !uid || h.user_id === uid
}

// Instant, no-network read for painting the UI before any auth/network work.
const getCached = (): UserHandle | null => (cached && isMine(cached) ? cached : null)

// The caller's sharing handle (e.g. "moon-light-456"), minted server-side on
// first request. Needs an auth session (see ensureSession).
const getMine = async (): Promise<UserHandle | null> => {
    if (cached && isMine(cached)) return cached
    try {
        cached = await utils.http.get<UserHandle>('/api/v1/handles/me')
        ldb.set(KEY, cached)
        return cached
    } catch (err) {
        console.warn('Failed to load sharing handle:', err)
        return null
    }
}

// Set/clear the public display name shown in others' share requests.
const updateDisplayName = async (display_name: string | null): Promise<UserHandle> => {
    cached = await utils.http.put<UserHandle>('/api/v1/handles/me', { display_name })
    ldb.set(KEY, cached)
    return cached
}

export default { getMine, getCached, updateDisplayName }
